import { burl, EXTERNAL_API_URL } from '@/config/site';
import axios from 'axios';
import toast from 'react-hot-toast';
import sha256 from 'crypto-js/sha256';
import { useUserStore } from '@/components/login/loginStore';
import { t } from "@/utils/i18n";

type CallbackFunction = (data: any) => void;


function hashData(data: any) {
    let dataString = JSON.stringify(data);
    return sha256(dataString).toString();
}
function addPostHashToGETParam(endpoint: string, data: object) {
    let hash = hashData(data);
    if (endpoint.includes('?')) {
        endpoint += `&phstk=${hash}`;
    } else {
        endpoint += `?phstk=${hash}`;
    }
    return endpoint;
}

function addCCTKToGETParam(endpoint: string) {
    let cctk = useUserStore.getState().cctk;
    if ((endpoint.startsWith(EXTERNAL_API_URL) || endpoint.startsWith('http://localhost')) && cctk)
        if (endpoint.includes('?')) {
            endpoint += `&cctk=${cctk}`;
        } else {
            endpoint += `?cctk=${cctk}`;
        }
    return endpoint;
}

export const fetchData = async (
    endpoint: string,
    callback: CallbackFunction,
    showError: boolean = true,
    callbackError: CallbackFunction = (_s) => { },
) => {
    try {
        if (!endpoint.startsWith('http')) endpoint = burl(endpoint);
        endpoint = addCCTKToGETParam(endpoint);
        const response = await axios.get(endpoint, { withCredentials: true });

        if (endpoint.includes('.json')) callback(response.data);
        else callback(response.data['data']);
    } catch (error: any) {
        if (callbackError) callbackError(error);
        if (showError) {
            if (error.response && error.response.data && error.response.data.message) {
                toast.error(error.response.data.message);
            } else {
                toast.error(t('requests.requests_1'));
            }
            throw error;
        }
    }
};


export const sendData = async (
    endpoint: string,
    data: object | null,
    method: 'POST' | 'PUT' | 'DELETE' | 'PATCH',
    showError: boolean = true,
) => {
    try {
        if (!endpoint.startsWith('http')) endpoint = burl(endpoint);
        endpoint = addCCTKToGETParam(endpoint);
        if (data && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
            endpoint = addPostHashToGETParam(endpoint, data);
        }

        const config = { withCredentials: true, data };
        const response = await axios({
            method,
            url: endpoint,
            ...config
        });

        return response.data?.data || response.data;
    } catch (error: any) {
        if (showError) {
            if (error.response && error.response.data && error.response.data.message) {
                toast.error(error.response.data.message);
            } else {
                toast.error(t('requests.requests_1'));
            }
        }
        throw error;
    }
};


export const postData = async (
    endpoint: string,
    data: object,
    callback: CallbackFunction,
    showError: boolean = true,
    callbackError: CallbackFunction = (_s) => { },
) => {
    try {
        if (!endpoint.startsWith('http')) endpoint = burl(endpoint);
        endpoint = addPostHashToGETParam(endpoint, data);
        endpoint = addCCTKToGETParam(endpoint);
        const response = await axios.post(endpoint, data, { withCredentials: true });

        if (endpoint.includes('.json')) callback(response.data);
        else callback(response.data['data']);
    } catch (error: any) {
        if (callbackError) callbackError(error);
        if (showError) {
            if (error.response && error.response.data && error.response.data.message) {
                toast.error(error.response.data.message);
            } else {
                toast.error(t('requests.requests_1'));
            }
            throw error;
        }
    }
};

export const fetchWithArgs = async (
    endpoint: string,
    args: { [key: string]: any },
    callback: CallbackFunction,
    showError: boolean = true,
    callbackError: CallbackFunction = (_s) => { },
) => {
    // this is meant to be a proxy function for easier conversion of POST requests to GET requests
    // data should be an object with the GET parameters

    let appendage = '';
    Object.keys(args).forEach(key => appendage += `${key}=${args[key]}&`);
    appendage = appendage.slice(0, -1);
    if (endpoint.includes('?')) endpoint += `&${appendage}`;
    else endpoint += `?${appendage}`;
    fetchData(endpoint, callback, showError, callbackError);
};


export const postFormData = async (
    endpoint: string,
    data: object,
    callback: CallbackFunction,
    showError: boolean = true,
    callbackError: CallbackFunction = (_s) => { },
) => {
    try {
        if (!endpoint.startsWith('http')) endpoint = burl(endpoint);
        const response = await axios.post(endpoint, data, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
        callback(response.data['data']);
    } catch (error: any) {
        if (callbackError) callbackError(error);
        if (showError) {
            if (error.response && error.response.data && error.response.data.message) {
                toast.error(error.response.data.message);
            } else {
                toast.error(t('requests.requests_1'));
            }
            throw error;
        }
    }
}
